import React, { ReactNode, useRef, useEffect, useState } from 'react';
import './Modal.scss';
import { IconCancel, IconPendoInfo } from 'components/Icons';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode | null;
  width?: string | number;
  height?: string | number;
  className?: string;
}

/**
 * A reusable modal component that can be used across the application.
 * It handles the overlay, positioning, and basic styling of a modal.
 * The modal is constrained to the width of the AIAnalysisModule.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children = null,
  width = 'auto',
  height = 'auto',
  className = '',
}) => {
  const [moduleWidth, setModuleWidth] = useState<number | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Find the parent AIAnalysisModule element to get its width
      const moduleElement = document.querySelector('.ai-analysis-module');
      if (moduleElement) {
        setModuleWidth(moduleElement.clientWidth);
      }

      // Update width on resize
      const handleResize = () => {
        const moduleElement = document.querySelector('.ai-analysis-module');
        if (moduleElement) {
          setModuleWidth(moduleElement.clientWidth);
        }
      };

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Handle click on the overlay (outside the modal content)
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevent clicks inside the modal from closing it
  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  // Calculate content width based on module width
  const contentWidth = moduleWidth
    ? typeof width === 'number'
      ? Math.min(width, moduleWidth - 48)
      : Math.min(500, moduleWidth - 48)
    : width;

  return (
    <div className="ai-analysis-modal-overlay" onClick={handleOverlayClick} ref={overlayRef}>
      <div
        className={`ai-analysis-modal-content ${className}`}
        onClick={handleContentClick}
        style={{
          width: contentWidth,
          height,
          maxWidth: moduleWidth ? `${moduleWidth - 48}px` : '500px',
        }}
      >
        {title && (
          <div className="ai-analysis-modal-title">
            <div className="ai-analysis-modal-title__icon">
              <IconPendoInfo />
            </div>
            <div className="ai-analysis-modal-title__text">{title}</div>
            <div className="ai-analysis-modal-title__close" onClick={onClose}>
              <IconCancel />
            </div>
          </div>
        )}
        {children ? (
          <div className="ai-analysis-modal-body">{children}</div>
        ) : (
          <div className="ai-analysis-modal-body">No additional information available</div>
        )}
      </div>
    </div>
  );
};

export default Modal;
